import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  apiService,
  ClassSummary,
  ListClassesResponse,
  ListSectionsResponse,
  SchoolBranchesJson,
  SectionSummary,
} from '../services/api';
import { TENANT_CREDENTIALS_EVENT } from '../config/api-config';
import { useAuth } from './AuthContext';

type ReferenceSlice<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

type ReferenceDataContextValue = {
  classes: ReferenceSlice<ListClassesResponse>;
  sections: ReferenceSlice<ListSectionsResponse>;
  branches: ReferenceSlice<SchoolBranchesJson>;
  reloadClasses: () => Promise<void>;
  reloadSections: () => Promise<void>;
  reloadBranches: () => Promise<void>;
  reloadAll: () => Promise<void>;
  /** Filter cached sections for a class (client-side until API supports class_id filter). */
  sectionsForClass: (classId: string) => SectionSummary[];
};

const defaultSlice = <T,>(): ReferenceSlice<T> => ({
  data: null,
  loading: false,
  error: null,
});

const ReferenceDataContext = createContext<ReferenceDataContextValue | null>(null);

/** Module codes that only exist for student/guardian portal sessions. */
const PORTAL_MODULE_CODES = new Set(['portal', 'student_portal', 'parent_portal']);

export function ReferenceDataProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  // Backend allows GET /classes and GET /sections for class:manage OR student:read OR
  // fees:read (fee counter / accountant dropdowns). Portal users use portal APIs.
  const canLoadClassesSections =
    !isSuperAdmin &&
    ['class:manage', 'student:read', 'fees:read'].some((code) =>
      (user?.permissions ?? []).includes(code)
    );
  // Branches back staff-side UIs; skip for sessions whose DB access is portal-only.
  const canLoadBranches =
    !isSuperAdmin &&
    (user?.moduleAccess ?? []).some(
      (entry) => !PORTAL_MODULE_CODES.has(String(entry.module_code ?? '').trim().toLowerCase())
    );
  const skipStaffReferenceData = !canLoadClassesSections && !canLoadBranches;
  const [classes, setClasses] = useState<ReferenceSlice<ListClassesResponse>>(defaultSlice);
  const [sections, setSections] = useState<ReferenceSlice<ListSectionsResponse>>(defaultSlice);
  const [branches, setBranches] = useState<ReferenceSlice<SchoolBranchesJson>>(defaultSlice);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reloadClasses = useCallback(async () => {
    if (!canLoadClassesSections) return;
    setClasses((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const classes = await apiService.listAllClasses();
      if (!mountedRef.current) return;
      setClasses({
        data: {
          classes,
          total: classes.length,
          page: 1,
          per_page: classes.length || 1,
          total_pages: 1,
        },
        loading: false,
        error: null,
      });
    } catch (e) {
      if (!mountedRef.current) return;
      setClasses({
        data: null,
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load classes',
      });
    }
  }, [canLoadClassesSections]);

  const reloadSections = useCallback(async () => {
    if (!canLoadClassesSections) return;
    setSections((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const sections = await apiService.listAllSections();
      if (!mountedRef.current) return;
      setSections({
        data: {
          sections,
          total: sections.length,
          page: 1,
          per_page: sections.length || 1,
          total_pages: 1,
        },
        loading: false,
        error: null,
      });
    } catch (e) {
      if (!mountedRef.current) return;
      setSections({
        data: null,
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load sections',
      });
    }
  }, [canLoadClassesSections]);

  const reloadBranches = useCallback(async () => {
    if (!canLoadBranches) return;
    setBranches((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await apiService.listSchoolBranches();
      if (!mountedRef.current) return;
      setBranches({ data: res.data, loading: false, error: null });
    } catch (e) {
      if (!mountedRef.current) return;
      setBranches({
        data: null,
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load branches',
      });
    }
  }, [canLoadBranches]);

  const reloadAll = useCallback(async () => {
    if (skipStaffReferenceData) return;
    await Promise.all([reloadClasses(), reloadSections(), reloadBranches()]);
  }, [skipStaffReferenceData, reloadClasses, reloadSections, reloadBranches]);

  useEffect(() => {
    if (!canLoadClassesSections) {
      setClasses(defaultSlice());
      setSections(defaultSlice());
    }
    if (!canLoadBranches) {
      setBranches(defaultSlice());
    }
    if (skipStaffReferenceData) return;
    void reloadAll();
  }, [skipStaffReferenceData, canLoadClassesSections, canLoadBranches, reloadAll]);

  useEffect(() => {
    const onCredentialsChanged = () => void reloadAll();
    window.addEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
    return () => window.removeEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
  }, [reloadAll]);

  const sectionsForClass = useCallback(
    (classId: string): SectionSummary[] => {
      const all = sections.data?.sections ?? [];
      if (!classId) return all;
      return all.filter((s) => s.class_id === classId);
    },
    [sections.data?.sections]
  );

  const value = useMemo(
    (): ReferenceDataContextValue => ({
      classes,
      sections,
      branches,
      reloadClasses,
      reloadSections,
      reloadBranches,
      reloadAll,
      sectionsForClass,
    }),
    [classes, sections, branches, reloadClasses, reloadSections, reloadBranches, reloadAll, sectionsForClass]
  );

  return <ReferenceDataContext.Provider value={value}>{children}</ReferenceDataContext.Provider>;
}

export function useReferenceData(): ReferenceDataContextValue {
  const ctx = useContext(ReferenceDataContext);
  if (!ctx) {
    throw new Error('useReferenceData must be used within ReferenceDataProvider');
  }
  return ctx;
}

/** Returns null when ReferenceDataProvider is not mounted (e.g. tests). */
export function useReferenceDataOptional(): ReferenceDataContextValue | null {
  return useContext(ReferenceDataContext);
}

/** Cached class list (empty array while loading). */
export function useCachedClasses(): { classes: ClassSummary[]; loading: boolean; error: string | null } {
  const { classes } = useReferenceData();
  return {
    classes: classes.data?.classes ?? [],
    loading: classes.loading,
    error: classes.error,
  };
}
